import { useState } from 'react';
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconArrowRight, IconDownload } from '@tabler/icons-react';
import { format } from 'date-fns';
import { TICKET_TYPE, TICKET_TYPE_CONFIG } from '../../config';
import { toStartOfDayISO, toEndOfDayISO } from '../../utils';

const todayStr = () => format(new Date(), 'yyyy-MM-dd');

export type ExportCompletedTicketRange = {
  from: string;
  to: string;
};

type ExportCompletedTicketButtonProps = {
  ticketType: TICKET_TYPE;
  loading: boolean;
  onExportSubmit: (range: ExportCompletedTicketRange) => void;
};

export function ExportCompletedTicketButton({
  ticketType,
  loading,
  onExportSubmit,
}: ExportCompletedTicketButtonProps) {
  const [opened, setOpened] = useState(false);
  const [from, setFrom] = useState<string | null>(todayStr());
  const [to, setTo] = useState<string | null>(todayStr());

  const close = () => setOpened(false);

  const handleConfirm = () => {
    if (!from || !to) return;
    onExportSubmit({
      from: toStartOfDayISO(from),
      to: toEndOfDayISO(to),
    });
    close();
  };

  return (
    <>
      <Button
        display="flex"
        justify="flex-end"
        mx="auto 0"
        leftSection={<IconDownload size={16} />}
        variant="outline"
        size="sm"
        loading={loading}
        onClick={() => {
          setFrom(todayStr());
          setTo(todayStr());
          setOpened(true);
        }}
      >
        匯出已繳清單
      </Button>

      <Modal
        opened={opened}
        onClose={close}
        title={
          <Text fw={600} size="lg">
            匯出
            <span className="text-blue-400">
              {TICKET_TYPE_CONFIG[ticketType].name}
            </span>
            已繳清單
          </Text>
        }
        size="lg"
      >
        <Stack gap="md">
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              匯出起迄日{' '}
              <Text component="span" c="red">
                *
              </Text>
            </Text>
            <Group gap="xs" align="center" wrap="nowrap">
              <DatePickerInput
                flex={1}
                valueFormat="YYYY-MM-DD"
                placeholder="請選擇起始日"
                value={from}
                maxDate={to ?? undefined}
                onChange={setFrom}
                clearable
              />
              <IconArrowRight size={16} color="var(--mantine-color-dimmed)" />
              <DatePickerInput
                flex={1}
                valueFormat="YYYY-MM-DD"
                placeholder="請選擇結束日"
                value={to}
                minDate={from ?? undefined}
                onChange={setTo}
                clearable
              />
            </Group>
          </Stack>

          <Group justify="flex-end" mt="sm">
            <Button variant="outline" onClick={close}>
              取消
            </Button>
            <Button disabled={!from || !to} onClick={handleConfirm}>
              確認
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
